import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import styled, { useTheme } from "styled-components/macro";
import useSWR from "swr";
import { endpoints } from "../../endpoints";
import { Form, FormFieldAndIcon } from "../../layout/FormLayout";
import { AddOrEditImageAttribute } from "../../pages/admin/SellerAdmin/PIM/SellerAdminPIMAttributes/ImageAttributes/AddOrEditImageAttribute";
import { stringArrayToOptionArray } from "../../pages/admin/SellerAdmin/PIM/SellerAdminTemplates/AddItemToTemplateCollectionForm";
import type {
  CreatedOption,
  MethodsOfUseForm,
  OptionType,
  UUID,
} from "../../types/types";
import { isCreatedOption } from "../../types/types";
import type {
  Assets,
  AttributeSchema,
  ImageAttributeValue,
  PIMProduct,
} from "../../types/types.PIM";
import { useStoreState } from "../../util/util";
import { LinkAttributeValueSchema } from "../../util/zod.util";
import { useAuthContext } from "../Auth";
import {
  ButtonWithConfirmDialog,
  DestructiveButtonLargeV2,
  PrimaryButtonLargeV2,
  SecondaryButtonMediumV2,
  XSmallButtonRow,
} from "../Buttons/Buttons";
import type { ChipType } from "../Chips/Chips";
import { CreatableSelectBox } from "../CreateableSelectBox/CreatableSelectBox";
import { DatePicker } from "../DatePicker/DatePicker";
import { DeletableChips } from "../DeletableChips/DeletableChips";
import { ErrorPlaceholder } from "../Error";
import {
  InfoIcon,
  LoadingIcon,
  NonVisibleIcon,
  NotApplicableIcon,
} from "../Icons/Icons";
import { RichEditor } from "../RichEditor/RichEditor";
import { SearchSelect } from "../SearchSelect/SearchSelect";
import { SearchSelectInfiniteScroll } from "../SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { SlideOut } from "../SlideOut/SlideOut";
import { TextArea } from "../TextArea/TextArea";
import { TextField } from "../TextFields/TextFields";
import { ToggleSwitchV2 } from "../ToggleSwitch/ToggleSwitch";
import { SoftHeaderRegular } from "../Typography/Typography";
import { ImageAttributePreview } from "./ImageAttributePreview";
import { PlusIcon } from "../Icons/Icons";
import type { ProductGroupFormValues } from "../../pages/SharedPages/ProductDetailPage/ProductOverview/product_overview.utils";

const InputWrapper = styled.div`
  width: 100%;
  max-width: 520px;
  margin-bottom: 10px;
`;

const CenteredIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: start;
  padding-top: 12px;
`;

const FlexForm = styled(Form)`
  display: flex;
  flex-direction: column;
`;

export const LinkTextFieldsContainer = styled.div`
  max-width: 500px;
  display: grid;
  grid-template-columns: 48% auto 48%;
  flex-wrap: nowrap;
  overflow: hidden;
`;

export const GreyLineWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const GreyLine = styled.div`
  height: 1px;
  width: 100%;
  border: solid 1px ${({ theme }) => theme.primaryBorder};
`;

const LoadingIconContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`;

const RichEditorLocalContainer = styled.div`
  .ql-container {
    height: fit-content;
  }
`;

const ImageAttribute = ({
  attribute,
  methodsOfUseForm,
  onCreateImageAttribute,
  isDisabled,
  isNotApplicable,
}: {
  attribute: AttributeSchema;
  methodsOfUseForm: MethodsOfUseForm;
  onCreateImageAttribute?: (
    image_arr: ImageAttributeValue | null,
    attribute: AttributeSchema
  ) => void;
  isDisabled?: boolean;
  isNotApplicable?: boolean;
}) => {
  const [showSlideout, setShowSlideout] = useState(false);
  const [imageAttribute, setImageAttribute] =
    useState<ImageAttributeValue | undefined>();
  const [isDeleted, setIsDeleted] = useState(false);
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();

  const assetId = useMemo(() => {
    if (imageAttribute?.asset_id) {
      return imageAttribute.asset_id;
    }
    if (attribute.values?.length && !isDeleted) {
      return (attribute.values[0].value as ImageAttributeValue).asset_id;
    }
    return null;
  }, [imageAttribute?.asset_id, attribute.values, isDeleted]);

  const { data: asset, error: assetError } = useSWR<Assets>(
    assetId ? endpoints.v2_tenants_id_pim_assets_id(tenant_id, assetId) : null,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000, // 1 minute
    }
  );

  const closeSlideout = useCallback(() => {
    setShowSlideout(false);
  }, []);

  const onComplete = useCallback(
    (imageAttr: ImageAttributeValue | null, canClose = true) => {
      setImageAttribute(imageAttr ?? undefined);
      setIsDeleted(!imageAttr);
      onCreateImageAttribute?.(imageAttr, attribute);
      if (canClose) {
        closeSlideout();
      }
    },
    [onCreateImageAttribute, attribute, closeSlideout]
  );

  useEffect(() => {
    methodsOfUseForm.register({
      name: attribute.id,
      required: attribute.is_required,
    });

    if (!imageAttribute && !isDeleted) {
      const defaultValue = attribute.values?.length
        ? (attribute.values[0].value as ImageAttributeValue)
        : undefined;
      methodsOfUseForm.setValue(attribute.id, defaultValue);
    }
  }, [
    attribute.id,
    attribute.is_required,
    attribute.values,
    imageAttribute,
    isDeleted,
    methodsOfUseForm,
  ]);

  const isLoadingAsset = !assetError && !asset && assetId;

  if (isLoadingAsset) {
    return (
      <LoadingIconContainer>
        <LoadingIcon width={24} height={24} />
      </LoadingIconContainer>
    );
  }

  if (assetError) {
    return <ErrorPlaceholder message={t("Could not load image")} />;
  }

  if (assetId && asset && !isNotApplicable) {
    return (
      <ImageAttributePreview
        attribute={attribute}
        asset={asset}
        onCreateImageAttribute={(imageAttr) => {
          onComplete(imageAttr, false);
        }}
        image_attribute_value={imageAttribute}
      />
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "fit-content",
          gap: "8px",
        }}
      >
        <SoftHeaderRegular>{attribute.name}</SoftHeaderRegular>
        <SecondaryButtonMediumV2
          type="button"
          onClick={() => setShowSlideout(true)}
          disabled={isDisabled || isNotApplicable}
        >
          <PlusIcon />
          {t("Add Image")}
        </SecondaryButtonMediumV2>
      </div>
      <SlideOut show={showSlideout} closeFlyout={closeSlideout}>
        <AddOrEditImageAttribute
          type="add"
          attribute={attribute}
          onCancel={closeSlideout}
          onComplete={onComplete}
          existing_asset={!isDeleted ? asset : undefined}
        />
      </SlideOut>
    </>
  );
};

export const FormFieldAndIcons = ({
  description,
  restricted,
  children,
  attribute,
  applicable,
  disable_applicable_button,
  onClickNotApplicable,
  pimProductType,
}: {
  description?: string | null;
  restricted?: boolean;
  children: ReactNode;
  attribute: AttributeSchema;
  applicable?: boolean;
  disable_applicable_button?: boolean;
  onClickNotApplicable: () => void;
  pimProductType: PIMProduct["pim_product_type"];
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasPermission } = useAuthContext();

  const is_master_variant_attribute_disabled_tooltip =
    attribute.object_type === "product_name"
      ? ""
      : attribute.is_master && pimProductType === "variant"
      ? t(
          "This is a master attribute, and is editable only on a master product."
        )
      : !attribute.is_master && pimProductType === "master"
      ? t(
          "This is a variant attribute, and is editable only on a variant product"
        )
      : "";

  return (
    <FormFieldAndIcon>
      <div
        data-tip={is_master_variant_attribute_disabled_tooltip}
        data-for="master-variant-tip"
      >
        {children}
      </div>
      {description && (
        <>
          <CenteredIconContainer data-tip={description} data-for="info-tip">
            <InfoIcon fill={theme.secondaryIconColor} />
          </CenteredIconContainer>
          <ReactTooltip
            delayHide={500}
            id="info-tip"
            clickable
            effect="solid"
          />
        </>
      )}
      {restricted && (
        <>
          <CenteredIconContainer
            data-for="restricted-tip"
            data-tip={t("This attribute is only visible for internal users")}
          >
            <NonVisibleIcon
              width={24}
              height={24}
              fill={theme.secondaryIconColor}
            />
          </CenteredIconContainer>
          <ReactTooltip delayHide={500} id="restricted-tip" effect="solid" />
        </>
      )}
      {!attribute.is_mandatory && !attribute.is_required && (
        <>
          <ButtonWithConfirmDialog
            Button={({ onClick }) => (
              <CenteredIconContainer
                as="button"
                type="button"
                disabled={
                  !hasPermission("modify_products") || disable_applicable_button
                }
                onClick={onClick}
                css={`
                  background: none;
                  border: none;
                  margin-top: 12px;
                  width: 24px;
                  height: 24px;
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0;
                  cursor: ${({ disabled }: { disabled?: boolean }) =>
                    disabled ? "not-allowed" : "pointer"};
                `}
              >
                <NotApplicableIcon
                  width={24}
                  height={24}
                  fill={
                    !applicable ? theme.errorColor : theme.secondaryIconColor
                  }
                />
              </CenteredIconContainer>
            )}
            testid="not-applicable-button"
            confirmMessage={t(
              applicable
                ? "Are you sure you want to mark this field as not applicable?"
                : "Are you sure you want to mark this field as applicable?"
            )}
            handleConfirm={() => onClickNotApplicable()}
            datatip={
              hasPermission("modify_products")
                ? t("Mark as not applicable")
                : t(
                    "You don't have permission to mark attributes as not applicable"
                  )
            }
            datafor="not-applicable-tip"
          />
          <ReactTooltip
            delayHide={500}
            id="not-applicable-tip"
            effect="solid"
          />
        </>
      )}
      <ReactTooltip id="master-variant-tip" effect="solid" />
    </FormFieldAndIcon>
  );
};

export type AttributeGroupFormGeneratorProps = {
  attributes: AttributeSchema[];
  methodsOfUseForm: MethodsOfUseForm;
  onSubmit: (values: ProductGroupFormValues) => Promise<JSX.Element | void>;
  onCancel: () => void;
  isLoading: boolean;
  onCreateOption: (value: string, attribute: AttributeSchema) => Promise<void>;
  onCreateImageAttribute?: (
    image_arr: ImageAttributeValue | null,
    attribute: AttributeSchema
  ) => void;
  showMandatoryAsterisk?: boolean;
  pimProductType?: PIMProduct["pim_product_type"];
  updateApplicability: (
    attributesToMakeApplicable: UUID[],
    attributesToMakeUnapplicable: UUID[]
  ) => Promise<void>;
};

// Helper function to format label with asterisk if mandatory
const formatLabel = (
  attr: AttributeSchema,
  t: (key: string | string[]) => string,
  showMandatoryAsterisk?: boolean
) => {
  const displayName = attr.display_name ? t([attr.display_name]) : attr.name;
  return attr.is_mandatory && showMandatoryAsterisk
    ? `${displayName} *`
    : displayName;
};

/**
 * Takes in columns and outputs a form. Supports all values of @see AttributeInput
 */
export function AttributeGroupFormGenerator({
  attributes,
  methodsOfUseForm,
  onSubmit,
  onCancel,
  onCreateOption,
  onCreateImageAttribute,
  isLoading,
  showMandatoryAsterisk,
  pimProductType = "standard",
  updateApplicability,
}: AttributeGroupFormGeneratorProps): JSX.Element {
  const {
    register,
    formState,
    errors,
    control,
    setValue,
    handleSubmit,
    watch,
  } = methodsOfUseForm;
  const [attribute_to_update, set_attribute_to_update] = useState<string>();
  const [disable_applicable_button, set_disable_applicable_button] =
    useState(false);
  const { t } = useTranslation();
  const { hasPermission } = useAuthContext();

  const multiSelectRefs = useRef<{ [key: string]: { clearChips: () => void } }>(
    {}
  );

  const datePickerRef = useRef<{ updateDate: (date: string) => void }>(null);

  const onClickNotApplicable = async (attr: AttributeSchema) => {
    const attributesToMakeApplicable: UUID[] = [];
    const attributesToMakeUnapplicable: UUID[] = [];
    attributes.forEach(({ is_not_applicable, id }) => {
      if (id === attr.id) {
        // Toggle applicability for the clicked attribute
        (is_not_applicable
          ? attributesToMakeApplicable
          : attributesToMakeUnapplicable
        ).push(id);
      } else {
        // Keep existing state for other attributes
        (is_not_applicable
          ? attributesToMakeUnapplicable
          : attributesToMakeApplicable
        ).push(id);
      }
    });
    set_disable_applicable_button(true);
    await updateApplicability(
      attributesToMakeApplicable,
      attributesToMakeUnapplicable
    );
    const newNotApplicableState = !attr.is_not_applicable;
    if (newNotApplicableState) {
      switch (attr?.input_type) {
        case "date":
          methodsOfUseForm.setValue(attr.id, "", {
            shouldValidate: true,
            shouldDirty: true,
          });
          break;
        case "form_field":
          methodsOfUseForm.setValue(attr.id, "", {
            shouldValidate: true,
            shouldDirty: true,
          });
          break;
        case "multiline_entry":
          methodsOfUseForm.setValue(attr.id, "", {
            shouldValidate: true,
            shouldDirty: true,
          });

          break;
        case "multi_select":
          methodsOfUseForm.setValue(attr.id, []);
          multiSelectRefs.current[attr.id]?.clearChips();
          break;
        case "link":
          setTimeout(() => {
            setValue(
              attr.id,
              { display_text: "", url: "" },
              {
                shouldValidate: true,
                shouldDirty: true,
              }
            );
          }, 0);

          break;
        case "product_reference":
          methodsOfUseForm.setValue(
            attr.id,
            attr.is_multiple_choice ? [] : "",
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          );
          break;
        default:
          methodsOfUseForm.setValue(attr.id, "");
      }
      set_disable_applicable_button(false);
    } else {
      set_attribute_to_update(attr.id);
    }
  };

  const update_attribute = useCallback(
    (attr_id: string, attributes: AttributeSchema[]) => {
      const attr = attributes.find((a) => a.id === attr_id);
      if (attr) {
        switch (attr.input_type) {
          case "link": {
            const originalValue = attr.values?.[0]?.value;
            const parsedLink =
              LinkAttributeValueSchema.safeParse(originalValue);
            setValue(
              attr.id,
              parsedLink.success
                ? parsedLink.data
                : { display_text: "", url: "" },
              { shouldValidate: true, shouldDirty: true }
            );
            break;
          }
          case "date": {
            if (attr.values?.length) {
              const dateValue = attr.values[0].value as string;
              setValue(attr.id, dateValue, {
                shouldValidate: true,
                shouldDirty: true,
              });
              datePickerRef.current?.updateDate(dateValue);
            }
            break;
          }
          case "single_select": {
            if (attr.values?.length) {
              const variant = attr.values[0].enum_variant?.variant;
              setValue(
                attr.id,
                { label: variant, value: variant },
                { shouldValidate: true, shouldDirty: true }
              );
            }
            break;
          }
          case "toggle":
          case "checkbox": {
            if (attr.values?.length) {
              setValue(attr.id, attr.values[0].value as boolean, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
            break;
          }
          case "multi_select": {
            if (attr.values?.length) {
              const values = attr.values.map(
                (v) => v.enum_variant?.variant ?? v.attribute_id
              );
              setValue(attr.id, values, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
            break;
          }
          case "multiline_entry": {
            if (attr.values?.length) {
              setValue(attr.id, attr.values[0].value as string, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
            break;
          }
          case "product_reference": {
            if (!attr.values?.length) break;

            if (attr.is_multiple_choice) {
              const values = attr.values.map((val) => ({
                label: val.product_ref?.name ?? val.attribute_id,
                value: val.product_ref?.id,
              }));
              setValue(attr.id, values, {
                shouldValidate: true,
                shouldDirty: true,
              });
            } else if (attr.values[0].product_ref) {
              setValue(
                attr.id,
                {
                  label: attr.values[0].product_ref.name,
                  value: attr.values[0].product_ref.id,
                },
                { shouldValidate: true, shouldDirty: true }
              );
            }
            break;
          }
        }
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (attribute_to_update) {
      update_attribute(attribute_to_update, attributes);
      set_attribute_to_update(undefined);
      set_disable_applicable_button(false);
    }
  }, [attribute_to_update, attributes, update_attribute]);

  return (
    <FlexForm onSubmit={handleSubmit(onSubmit)}>
      {attributes.map((attr) => {
        const isDisabled =
          !attr.is_editable ||
          attr.is_not_applicable ||
          (attr.object_type !== "product_name" &&
            ((attr.is_master && pimProductType === "variant") ||
              (!attr.is_master && pimProductType === "master")));

        switch (attr.input_type) {
          case "date":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                disable_applicable_button={disable_applicable_button}
              >
                <DatePicker
                  isOutsideRange={() => false}
                  label={formatLabel(attr, t, showMandatoryAsterisk)}
                  name={attr.id}
                  defaultValue={
                    attr.values?.length
                      ? (attr.values[0].value as string)
                      : undefined
                  }
                  required={attr.is_required}
                  methodsOfUseForm={methodsOfUseForm}
                  disabled={isDisabled}
                  ref={datePickerRef}
                />
              </FormFieldAndIcons>
            );
          case "form_field": {
            const fieldKey = `${attr.id}-${
              attr.is_not_applicable ? "na" : "norm"
            }`;

            return (
              <FormFieldAndIcons
                key={attr.id}
                applicable={!attr.is_not_applicable}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                disable_applicable_button={disable_applicable_button}
              >
                <TextField
                  key={fieldKey}
                  name={attr.id}
                  label={formatLabel(attr, t, showMandatoryAsterisk)}
                  theref={register({
                    required: attr.is_required,
                  })}
                  defaultValue={
                    attr.values?.length
                      ? (attr.values[0].value as string)
                      : undefined
                  }
                  formState={formState}
                  errors={errors}
                  type="text"
                  readOnly={attr.object_type === "product_id" || isDisabled}
                />
              </FormFieldAndIcons>
            );
          }
          case "single_select":
            const choices = attr?.choices?.map(stringArrayToOptionArray) ?? [];
            const formatCreateLabel = (value: string) =>
              t(`Save "{{value}}" to {{list_name}}`, {
                value,
                list_name: attr.list_name,
              });

            const creatableProps = hasPermission("modify_lists")
              ? {
                  isCreatable: true,
                  onCreateOption: (value: string) => {
                    onCreateOption(value, attr);
                  },
                  createOptionPosition: "first",
                  isValidNewOption: (val: string) => {
                    return (
                      val.length < 50 &&
                      !choices
                        .map(({ value }) => value.toLowerCase())
                        .includes(val.toLowerCase()) &&
                      val !== ""
                    );
                  },
                  formatCreateLabel: formatCreateLabel,
                }
              : {};

            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                disable_applicable_button={disable_applicable_button}
              >
                <Controller
                  as={CreatableSelectBox}
                  control={control}
                  name={attr.id}
                  placeholder={formatLabel(attr, t, showMandatoryAsterisk)}
                  options={choices}
                  isClearable
                  rules={{
                    required: attr.is_required,
                  }}
                  defaultValue={
                    attr?.values?.length
                      ? {
                          label: attr.values[0].enum_variant?.variant,
                          value: attr.values[0].enum_variant?.variant,
                        }
                      : null
                  }
                  errors={errors}
                  formState={formState}
                  disabled={isDisabled}
                  {...creatableProps}
                />
              </FormFieldAndIcons>
            );
          case "toggle":
          case "checkbox":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                disable_applicable_button={disable_applicable_button}
              >
                <div>
                  <ToggleSwitchV2
                    label={formatLabel(attr, t, showMandatoryAsterisk)}
                    name={attr.id}
                    ref={register({ required: attr.is_required })}
                    defaultChecked={
                      attr.values && (attr.values[0]?.value as boolean)
                    }
                    disabled={isDisabled}
                  />
                </div>
              </FormFieldAndIcons>
            );
          case "numeric": {
            const fieldKey = `${attr.id}-${
              attr.is_not_applicable ? "na" : "norm"
            }`;

            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                disable_applicable_button={disable_applicable_button}
              >
                <TextField
                  key={fieldKey}
                  name={attr.id}
                  label={`${formatLabel(
                    attr,
                    t,
                    showMandatoryAsterisk
                  )} (number)`}
                  theref={register({
                    required: attr.is_required,
                  })}
                  defaultValue={
                    attr.values?.length
                      ? (attr.values[0].value as string)
                      : undefined
                  }
                  formState={formState}
                  errors={errors}
                  type="number"
                  readOnly={isDisabled}
                />
              </FormFieldAndIcons>
            );
          }
          case "multiline_entry":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                disable_applicable_button={disable_applicable_button}
              >
                {attr.name === "Meta Keywords" ||
                attr.name === "Meta Description" ? (
                  <TextArea
                    name={attr.id}
                    label={formatLabel(attr, t, showMandatoryAsterisk)}
                    theref={register({ required: attr.is_required })}
                    formState={formState}
                    required={attr.is_required}
                    defaultValue={
                      attr.values?.length
                        ? (attr.values[0].value as string)
                        : undefined
                    }
                    readOnly={isDisabled}
                  />
                ) : (
                  <RichEditorLocalContainer>
                    <RichEditor
                      key={`${attr.id}-${attr.is_not_applicable}`}
                      value={
                        attr.values?.length
                          ? (attr.values[0].value as string)
                          : undefined
                      }
                      name={attr.id}
                      label={formatLabel(attr, t, showMandatoryAsterisk)}
                      useFormMethods={{
                        register,
                        formState,
                        setValue,
                        errors,
                        watch,
                      }}
                      readOnly={isDisabled}
                    />
                  </RichEditorLocalContainer>
                )}
              </FormFieldAndIcons>
            );
          case "multi_select":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                disable_applicable_button={disable_applicable_button}
              >
                {hasPermission("modify_lists") ? (
                  <MultiSelectField
                    key={`${attr.id}-${attr.is_not_applicable}`}
                    onCreateOption={onCreateOption}
                    attribute={attr}
                    type={"group"}
                    methodsOfUseForm={methodsOfUseForm}
                    multiSelectRefs={multiSelectRefs}
                    isDisabled={isDisabled}
                    isNotApplicable={attr.is_not_applicable ?? false}
                  />
                ) : (
                  <MultiSelectField
                    key={`${attr.id}-${attr.is_not_applicable}`}
                    attribute={attr}
                    type={"group"}
                    methodsOfUseForm={methodsOfUseForm}
                    multiSelectRefs={multiSelectRefs}
                    isDisabled={isDisabled}
                    isNotApplicable={attr.is_not_applicable ?? false}
                  />
                )}
              </FormFieldAndIcons>
            );
          case "link":
            const fieldKey = `${attr.id}-${
              attr.is_not_applicable ? "na" : "norm"
            }`;

            const maybeDefaultValue = (() => {
              if (attr?.values?.[0]?.value && !attr.is_not_applicable) {
                const parsed = LinkAttributeValueSchema.safeParse(
                  attr.values[0]?.value
                );
                if (parsed.success) {
                  return parsed.data;
                }
              }
              return { url: "", display_text: "" };
            })();

            const link = watch(attr.id);

            const displayTextIsRequired = !!(link?.url || attr.is_required);
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
                disable_applicable_button={disable_applicable_button}
              >
                <Controller
                  key={fieldKey}
                  control={control}
                  name={attr.id}
                  defaultValue={maybeDefaultValue}
                  render={({ onChange, value: formValue }) => {
                    return (
                      <LinkTextFieldsContainer>
                        <TextField
                          label={`${formatLabel(
                            attr,
                            t,
                            showMandatoryAsterisk
                          )} - ${t("Display Text")}`}
                          name={"display_text"}
                          type={"text"}
                          theref={register({ required: displayTextIsRequired })}
                          formState={formState}
                          errors={errors}
                          defaultValue={maybeDefaultValue.display_text}
                          value={link?.display_text ?? ""}
                          onChange={(event) => {
                            const newValue = {
                              ...link,
                              display_text: event.target.value,
                            };

                            onChange(newValue);
                          }}
                          readOnly={isDisabled}
                        />
                        <GreyLineWrapper>
                          <GreyLine />
                        </GreyLineWrapper>
                        <TextField
                          name={""}
                          type={"url"}
                          label={`${formatLabel(
                            attr,
                            t,
                            showMandatoryAsterisk
                          )} - URL`}
                          formState={formState}
                          errors={errors}
                          defaultValue={maybeDefaultValue.url}
                          value={formValue?.url ?? ""}
                          onChange={(event) => {
                            const newValue = {
                              ...formValue,
                              url: event.target.value,
                            };

                            onChange(newValue);
                          }}
                          theref={register({ required: false })}
                          readOnly={isDisabled}
                        />
                      </LinkTextFieldsContainer>
                    );
                  }}
                />
              </FormFieldAndIcons>
            );
          case "product_reference":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
              >
                <ProductReferenceField
                  attribute={attr}
                  methodsOfUseForm={methodsOfUseForm}
                  isDisabled={isDisabled}
                  isNotApplicable={attr.is_not_applicable ?? false}
                />
              </FormFieldAndIcons>
            );
          case "image":
            return (
              <FormFieldAndIcons
                key={attr.id}
                description={attr.description ? t([attr.description]) : null}
                restricted={attr.is_restricted}
                applicable={!attr.is_not_applicable}
                onClickNotApplicable={() => onClickNotApplicable(attr)}
                attribute={attr}
                pimProductType={pimProductType}
              >
                <ImageAttribute
                  attribute={attr}
                  methodsOfUseForm={methodsOfUseForm}
                  onCreateImageAttribute={onCreateImageAttribute}
                  isDisabled={isDisabled}
                  isNotApplicable={attr.is_not_applicable ?? false}
                />
              </FormFieldAndIcons>
            );
          default:
            return <></>; //assertUnreachable(attr);
        }
      })}
      <XSmallButtonRow>
        <DestructiveButtonLargeV2 onClick={onCancel} type="reset">
          {t("Cancel")}
        </DestructiveButtonLargeV2>

        <PrimaryButtonLargeV2 loading={isLoading}>
          {t("Save")}
        </PrimaryButtonLargeV2>
      </XSmallButtonRow>
    </FlexForm>
  );
}

export function MultiSelectField({
  type,
  attribute,
  methodsOfUseForm,
  displayByName,
  onCreateOption,
  multiSelectRefs,
  isDisabled,
  isNotApplicable,
}: {
  type: "group" | "collection";
  attribute: AttributeSchema;
  methodsOfUseForm: MethodsOfUseForm;
  displayByName?: boolean;
  onCreateOption?: AttributeGroupFormGeneratorProps["onCreateOption"];
  multiSelectRefs: React.MutableRefObject<{
    [key: string]: { clearChips: () => void };
  }>;
  isDisabled?: boolean;
  isNotApplicable: boolean;
}) {
  const [attributeChips, setAttributeChips] = useState<{ name: string }[]>([]);
  const [attributeOptions, setAttributeOptions] = useState<OptionType[]>(
    attribute?.choices?.map(stringArrayToOptionArray) ?? []
  );
  const { t } = useTranslation();
  const { register, setValue, errors, formState } = methodsOfUseForm;
  const handleMultiSelectChange = (data: CreatedOption | null) => {
    if (isCreatedOption(data)) {
      setAttributeChips((previous) => [
        ...previous,
        { name: data.label, id: data.value },
      ]);
      setAttributeOptions(
        (previous) =>
          previous?.filter((item) => item.label !== data.label) ?? []
      );
    }
  };
  useEffect(() => {
    if (isNotApplicable) {
      // Clear chips when not applicable
      setAttributeChips([]);
      setAttributeOptions(
        attribute?.choices?.map(stringArrayToOptionArray) ?? []
      );
    } else {
      // Initialize chips from values when applicable
      const getAttributeValuesChips = () => {
        const values =
          type === "group"
            ? attribute.values?.map((value) => {
                return {
                  name: value.enum_variant?.variant ?? value.attribute_id,
                };
              })
            : attribute.default_values?.map((value) => {
                return {
                  name: value.enum_variant?.variant ?? value.attribute_id,
                };
              });
        return values ?? [];
      };
      setAttributeChips(
        getAttributeValuesChips() ? getAttributeValuesChips() : []
      );
      setAttributeOptions(
        (previous) =>
          previous.filter(
            (item) =>
              (getAttributeValuesChips() as { name: string }[])
                .map((option) => option.name)
                .indexOf(item.value) < 0
          ) ?? []
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attribute, setValue, isNotApplicable]);

  useEffect(() => {
    if (attributeChips) {
      setValue(
        attribute.id,
        attributeChips.length
          ? attributeChips.map((attribute) => attribute.name)
          : undefined,
        { shouldValidate: true }
      );
    }
  }, [attributeChips, setValue, attribute]);

  const handleAttributeChipClick = (name: string) => {
    if (!attributeOptions.map(({ value }) => value).includes(name)) {
      setAttributeOptions([{ value: name, label: name }, ...attributeOptions]);
    }
    setAttributeChips(
      (previous) => previous?.filter((item) => item.name !== name) ?? []
    );
  };

  useEffect(() => {
    register({
      name: attribute.id,
      required: attribute.is_required,
    });
  }, [register, attribute]);

  const formatCreateLabel = (value: string) =>
    t(`Save "{{value}}" to {{list_name}}`, {
      value,
      list_name: attribute.list_name,
    });

  useEffect(() => {
    if (multiSelectRefs.current) {
      multiSelectRefs.current[attribute.id] = {
        clearChips: () => setAttributeChips([]),
      };
    }
  }, [attribute.id, multiSelectRefs]);

  return (
    <div>
      <InputWrapper>
        <SearchSelect
          name={attribute.id}
          options={attributeOptions}
          errors={errors}
          formState={formState}
          disabled={isDisabled || !attribute.is_editable}
          placeholder={t(`Select {{attribute}}`, {
            attribute: displayByName
              ? attribute.name
              : attribute.display_name
              ? t([attribute.display_name])
              : attribute.name,
          })}
          value={null}
          onChange={handleMultiSelectChange}
          {...(onCreateOption && {
            isCreatable: true,
            onCreateOption: (value: string) => {
              onCreateOption(value, attribute);
              handleMultiSelectChange({ label: value, value: value });
            },
            formatCreateLabel: formatCreateLabel,
            createOptionPosition: "first",
            isValidNewOption: (val: string) => {
              return (
                val.length < 50 &&
                !attributeOptions
                  .map(({ value }) => value.toLowerCase())
                  .includes(val.toLowerCase()) &&
                !attributeChips.find(
                  ({ name }) => name.toLowerCase() === val.toLowerCase()
                ) &&
                val !== ""
              );
            },
          })}
        />
      </InputWrapper>
      <DeletableChips
        chips={
          isDisabled || !attribute.is_editable
            ? [...attributeChips.map((chip) => ({ ...chip, active: false }))]
            : attributeChips
        }
        noneSelectedName={
          displayByName ? attribute.name : t([attribute.display_name])
        }
        handleClick={handleAttributeChipClick}
      />
    </div>
  );
}

export function ProductReferenceField({
  attribute,
  methodsOfUseForm,
  isDisabled,
  isNotApplicable,
}: {
  attribute: AttributeSchema;
  methodsOfUseForm: MethodsOfUseForm;
  isDisabled?: boolean;
  isNotApplicable: boolean;
}) {
  const [attributeChips, setAttributeChips] = useState<ChipType[]>([]);
  const { tenant_id } = useStoreState();
  const [, setAttributeOptions] = useState<OptionType[]>( //attributeOptions,
    attribute?.choices?.map(stringArrayToOptionArray) ?? []
  );
  const { t } = useTranslation();
  const { register, setValue, errors, formState, control } = methodsOfUseForm;
  const handleMultiSelectChange = (data: CreatedOption | null) => {
    if (isCreatedOption(data)) {
      setAttributeChips((previous) => [
        ...previous,
        { name: data.label, id: data.value },
      ]);
      setAttributeOptions(
        (previous) =>
          previous?.filter((item) => item.label !== data.label) ?? []
      );
    }
  };

  useEffect(() => {
    if (isNotApplicable) {
      // Clear chips when not applicable
      setAttributeChips([]);
      setAttributeOptions(
        attribute?.choices?.map(stringArrayToOptionArray) ?? []
      );
    } else {
      // Initialize chips from values when applicable
      const getAttributeValuesChips = () => {
        const values = attribute.values?.map((value) => {
          return {
            name: value.product_ref?.name ?? value.attribute_id,
            id: value.product_ref?.id,
          };
        });

        return values ?? [];
      };
      setAttributeChips(getAttributeValuesChips() ?? []);
      setAttributeOptions(
        (previous) =>
          previous.filter(
            (item) =>
              (getAttributeValuesChips() as { name: string; id: string }[])
                .map((option) => option)
                .indexOf(item.value) < 0
          ) ?? []
      );
    }
  }, [attribute?.choices, attribute.values, isNotApplicable]);

  useEffect(() => {
    if (attributeChips) {
      setValue(
        attribute.id,
        attributeChips.length
          ? attribute.is_multiple_choice
            ? attributeChips.map(({ id, name }) => ({ label: name, value: id }))
            : { label: attributeChips[0].name, value: attributeChips[0].id }
          : "",
        { shouldValidate: true }
      );

      setAttributeOptions(
        attribute?.choices
          ?.map(stringArrayToOptionArray)
          .filter(
            (item) =>
              attributeChips.map((option) => option.name).indexOf(item.value) <
              0
          ) ?? []
      );
    }
  }, [attributeChips, setValue, attribute]);

  const handleAttributeChipClick = (name: string) => {
    setAttributeChips(
      (previous) => previous?.filter((item) => item.name !== name) ?? []
    );
  };

  useEffect(() => {
    register({
      name: attribute.id,
      required: attribute.is_required,
    });
  }, [register, attribute]);

  return (
    <div>
      <InputWrapper>
        {attribute.is_multiple_choice ? (
          <>
            <InputWrapper>
              <SearchSelectInfiniteScroll
                name={attribute.id}
                key={attribute.id}
                isDisabled={isDisabled}
                params={(() => {
                  const params = new URLSearchParams();
                  params.append("order_by", "asc");
                  params.append("status", "published");
                  return params;
                })()}
                baseUrl={endpoints.v2_tenants_id_pim_products_summary(
                  tenant_id
                )}
                getOptions={(response: PIMProduct[]) =>
                  response
                    .reduce(
                      (
                        prev: { label: string; value: string }[],
                        { name, id }: PIMProduct
                      ) => [...prev, { label: name, value: id }],
                      []
                    )
                    .filter(
                      (item) =>
                        attributeChips
                          .map((option) => option.name)
                          .indexOf(item.label) < 0
                    )
                }
                onChange={handleMultiSelectChange}
                disabled={!attribute.is_editable}
                errors={errors}
                formState={formState}
                isClearable
                placeholder={
                  attribute.display_name
                    ? t([attribute.display_name])
                    : attribute.name
                }
                value={null}
                testid={attribute.name}
                autoComplete="on"
              />
            </InputWrapper>
            <DeletableChips
              chips={attributeChips}
              noneSelectedName="choices"
              handleClick={handleAttributeChipClick}
            />
          </>
        ) : (
          <Controller
            as={SearchSelectInfiniteScroll}
            name={attribute.id}
            control={control}
            key={attribute.id}
            errors={errors}
            formState={formState}
            isClearable
            disabled={isDisabled || !attribute.is_editable}
            placeholder={
              attribute.display_name
                ? t([attribute.display_name])
                : attribute.name
            }
            defaultValue={
              attribute?.values?.length
                ? {
                    label: attribute.values[0].product_ref?.name,
                    value: attribute.values[0].product_ref?.id,
                  }
                : ""
            }
            baseUrl={endpoints.v2_tenants_id_pim_products_summary(tenant_id)}
            params={(() => {
              const params = new URLSearchParams();
              params.append("order_by", "asc");
              params.append("status", "published");
              return params;
            })()}
            getOptions={(response: PIMProduct[]) =>
              response.reduce(
                (
                  prev: { label: string; value: string }[],
                  { name, id }: PIMProduct
                ) => [...prev, { label: name, value: id }],
                []
              )
            }
            onChange={(data: OptionType<string>) =>
              setValue(attribute.id, data)
            }
            testid={attribute.name}
          />
        )}
      </InputWrapper>
    </div>
  );
}
